import {
	PropositionProductType,
	PropositionProductTypeValues,
} from "@dune/types";
import { useMemo } from "react";
import { useMe } from "../../api/queries/users.queries";
import { RoleEnum } from "../../../../../packages/vars/src";

interface ProductTypeTab {
	value: PropositionProductTypeValues | undefined;
	label: string;
	count: number;
}

export const useProductTypeTabs = (
	productTypeCounts: Record<string, number>,
) => {
	const { me: user } = useMe();

	const brokerAccessibleProducts = useMemo(() => {
		if (user?.role !== RoleEnum.BROKER) return [];
		return (
			user.society?.brokerDetails?.accessibleProduct.filter(
				(product) => product.authorized,
			) ?? []
		);
	}, [user]);

	const insurerBinderTypes = useMemo(() => {
		if (user?.role !== RoleEnum.INSURER) return [];
		const binders =
			user.society?.insurerSociety?.flatMap((is) => is.binders) ?? [];
		return [...new Set(binders.map((b) => b.binderType).filter(Boolean))];
	}, [user]);

	const shouldShowProductTypeTabs = useMemo(() => {
		if (!user) return false;
		if (user.role === RoleEnum.SYNDIC) return false;
		if (user.role === RoleEnum.INSURER) {
			if (insurerBinderTypes.length === 1) {
				return false;
			}
		}
		return true;
	}, [user, insurerBinderTypes]);

	const productTypeTabs = useMemo(() => {
		const allTabs: ProductTypeTab[] = [
			{
				value: undefined,
				label: "Tous",
				count: productTypeCounts.all || 0,
			},
			{
				value: PropositionProductType.DO,
				label: "DO",
				count: productTypeCounts[PropositionProductType.DO] || 0,
			},
			{
				value: PropositionProductType.TRC,
				label: "TRC",
				count: productTypeCounts[PropositionProductType.TRC] || 0,
			},
			{
				value: PropositionProductType.MRT,
				label: "MRT",
				count: productTypeCounts[PropositionProductType.MRT] || 0,
			},
			{
				value: PropositionProductType.GFA,
				label: "GFA",
				count: productTypeCounts[PropositionProductType.GFA] || 0,
			},
		];

		if (user?.role === RoleEnum.BROKER) {
			const allowedProductTypes: PropositionProductTypeValues[] = [];
			for (const product of brokerAccessibleProducts) {
				if (product.warrantyType === "DO") {
					allowedProductTypes.push(PropositionProductType.DO);
				} else if (product.warrantyType === "TRC") {
					allowedProductTypes.push(PropositionProductType.TRC);
				} else if (product.warrantyType === "MRT") {
					allowedProductTypes.push(PropositionProductType.MRT);
				}
				if (product.gfaProduct === true) {
					allowedProductTypes.push(PropositionProductType.GFA);
				}
			}
			return allTabs.filter(
				(tab) =>
					tab.value === undefined || allowedProductTypes.includes(tab.value),
			);
		}

		if (user?.role === RoleEnum.INSURER) {
			const allowedProductTypes: PropositionProductTypeValues[] = [];
			for (const binderType of insurerBinderTypes) {
				if (binderType === "DOTRC") {
					allowedProductTypes.push(
						PropositionProductType.DO,
						PropositionProductType.TRC,
					);
				} else if (binderType === "MRT") {
					allowedProductTypes.push(PropositionProductType.MRT);
				} else if (binderType === "GFA") {
					allowedProductTypes.push(PropositionProductType.GFA);
				}
			}
			return allTabs.filter(
				(tab) =>
					tab.value === undefined || allowedProductTypes.includes(tab.value),
			);
		}

		return allTabs;
	}, [user, brokerAccessibleProducts, insurerBinderTypes, productTypeCounts]);

	return {
		productTypeTabs,
		shouldShowProductTypeTabs,
	};
};
